import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Package,
  Plus,
  AlertTriangle,
  ArrowUpCircle,
  ArrowDownCircle,
  Loader2,
  History,
  RefreshCw,
  Truck,
  Pencil,
  GripVertical,
  ArrowUp,
  ArrowDown,
  Save,
  FileSpreadsheet,
} from "lucide-react";
import api, { exportInventoryXlsx } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function InventoryPage() {
  const { isViewer } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showQuickBookingDialog, setShowQuickBookingDialog] = useState(false);
  const [isSortMode, setIsSortMode] = useState(false);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const [showCorrectionDialog, setShowCorrectionDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductCode, setSelectedProductCode] = useState("");
  const [bookingQuantity, setBookingQuantity] = useState("");
  const [bookingReason, setBookingReason] = useState("Lieferung");
  const [correctionStock, setCorrectionStock] = useState("");
  const [correctionReason, setCorrectionReason] = useState("Inventur");
  const [isBooking, setIsBooking] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    loadInventory();
  }, []);

  const handleExportXlsx = async () => {
    setIsExporting(true);
    try {
      const response = await exportInventoryXlsx();
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "").replace(/^(\d{8})(\d{4})$/, "$1_$2");
      link.download = `bestand_${ts}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Bestand als Excel exportiert");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Export fehlgeschlagen: " + (error.response?.data?.detail || error.message));
    } finally {
      setIsExporting(false);
    }
  };

  const loadInventory = async () => {
    try {
      const response = await api.get("/inventory");
      setInventory(response.data);
      if (response.data.length === 0) {
        // Try to initialize inventory if empty
        try {
          await api.post("/inventory/init");
          const retryResponse = await api.get("/inventory");
          setInventory(retryResponse.data);
        } catch (initError) {
          console.error("Init error:", initError);
        }
      }
    } catch (error) {
      console.error("Inventory load error:", error);
      toast.error("Bestand konnte nicht geladen werden: " + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const loadTransactions = async (productCode = null) => {
    try {
      const url = productCode 
        ? `/inventory-transactions?product_code=${productCode}&limit=50`
        : "/inventory-transactions?limit=50";
      const response = await api.get(url);
      setTransactions(response.data);
    } catch (error) {
      toast.error("Transaktionen konnten nicht geladen werden");
    }
  };

  const openBookingDialog = (product) => {
    setSelectedProduct(product);
    setBookingQuantity("");
    setBookingReason("Lieferung");
    setShowBookingDialog(true);
  };

  const openQuickBookingDialog = () => {
    setSelectedProductCode("");
    setBookingQuantity("");
    setBookingReason("Lieferung");
    setShowQuickBookingDialog(true);
  };

  const openCorrectionDialog = (product) => {
    setSelectedProduct(product);
    setCorrectionStock(product.current_stock.toString());
    setCorrectionReason("Inventur");
    setShowCorrectionDialog(true);
  };

  const openHistoryDialog = async (product = null) => {
    setSelectedProduct(product);
    await loadTransactions(product?.product_code);
    setShowHistoryDialog(true);
  };

  const handleBooking = async () => {
    if (!bookingQuantity || parseInt(bookingQuantity) <= 0) {
      toast.error("Bitte geben Sie eine gültige Menge ein");
      return;
    }

    setIsBooking(true);
    try {
      await api.post(`/inventory/${selectedProduct.product_code}/book`, {
        quantity: parseInt(bookingQuantity),
        reason: bookingReason || "Lieferung",
      });
      toast.success(`${bookingQuantity} Stück eingebucht`);
      setShowBookingDialog(false);
      loadInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Einbuchung fehlgeschlagen");
    } finally {
      setIsBooking(false);
    }
  };

  const handleQuickBooking = async () => {
    if (!selectedProductCode) {
      toast.error("Bitte wählen Sie ein Produkt aus");
      return;
    }
    if (!bookingQuantity || parseInt(bookingQuantity) <= 0) {
      toast.error("Bitte geben Sie eine gültige Menge ein");
      return;
    }

    setIsBooking(true);
    try {
      await api.post(`/inventory/${selectedProductCode}/book`, {
        quantity: parseInt(bookingQuantity),
        reason: bookingReason || "Lieferung",
      });
      const productName = inventory.find(i => i.product_code === selectedProductCode)?.product_name || selectedProductCode;
      toast.success(`${bookingQuantity} Stück ${productName} eingebucht`);
      setShowQuickBookingDialog(false);
      loadInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Einbuchung fehlgeschlagen");
    } finally {
      setIsBooking(false);
    }
  };

  const handleCorrection = async () => {
    const newStock = parseInt(correctionStock);
    if (isNaN(newStock) || newStock < 0) {
      toast.error("Bitte geben Sie einen gültigen Bestand ein (0 oder höher)");
      return;
    }

    setIsBooking(true);
    try {
      await api.post(`/inventory/${selectedProduct.product_code}/correct`, {
        new_stock: newStock,
        reason: correctionReason || "Korrektur",
      });
      toast.success(`Bestand auf ${newStock} Stück korrigiert`);
      setShowCorrectionDialog(false);
      loadInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Korrektur fehlgeschlagen");
    } finally {
      setIsBooking(false);
    }
  };

  const getLowStockCount = () => {
    return inventory.filter(item => item.current_stock < item.warning_threshold).length;
  };

  const getTotalStock = () => {
    return inventory.reduce((sum, item) => sum + item.current_stock, 0);
  };

  const moveItem = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= inventory.length) return;
    
    const newInventory = [...inventory];
    const temp = newInventory[index];
    newInventory[index] = newInventory[newIndex];
    newInventory[newIndex] = temp;
    
    setInventory(newInventory);
    setHasOrderChanged(true);
  };

  const saveSortOrder = async () => {
    try {
      const productCodes = inventory.map(item => item.product_code);
      await api.post("/inventory/sort", { product_codes: productCodes });
      toast.success("Sortierung gespeichert");
      setHasOrderChanged(false);
      setIsSortMode(false);
    } catch (error) {
      toast.error("Sortierung konnte nicht gespeichert werden");
    }
  };

  const cancelSortMode = () => {
    setIsSortMode(false);
    setHasOrderChanged(false);
    loadInventory(); // Reload original order
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Bestandsführung
          </h1>
          <p className="text-slate-500 text-sm">
            Verwalten Sie den Lagerbestand aller Komponenten
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isSortMode ? (
            <>
              {!isViewer && (
                <Button
                  onClick={openQuickBookingDialog}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={inventory.length === 0}
                >
                  <Truck className="w-4 h-4 mr-2" />
                  Lieferung einbuchen
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => openHistoryDialog(null)}
                className="border-slate-200"
              >
                <History className="w-4 h-4 mr-2" />
                Alle Buchungen
              </Button>
              <Button
                variant="outline"
                onClick={handleExportXlsx}
                disabled={isExporting || inventory.length === 0}
                className="border-slate-200"
                data-testid="export-inventory-xlsx"
                title="Aktuellen Bestand als Excel exportieren"
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                )}
                Export (.xlsx)
              </Button>
              {!isViewer && (
                <Button
                  variant="outline"
                  onClick={() => setIsSortMode(true)}
                  className="border-slate-200"
                  disabled={inventory.length === 0}
                >
                  <GripVertical className="w-4 h-4 mr-2" />
                  Sortieren
                </Button>
              )}
              <Button
                variant="outline"
                onClick={loadInventory}
                className="border-slate-200"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Aktualisieren
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={saveSortOrder}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={!hasOrderChanged}
              >
                <Save className="w-4 h-4 mr-2" />
                Speichern
              </Button>
              <Button
                variant="outline"
                onClick={cancelSortMode}
                className="border-slate-200"
              >
                Abbrechen
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Sort Mode Info */}
      {isSortMode && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-3">
            <div className="flex items-center gap-3">
              <GripVertical className="w-5 h-5 text-blue-600" />
              <p className="text-sm text-blue-800">
                <strong>Sortiermodus:</strong> Verwenden Sie die Pfeile, um die Reihenfolge der Produkte anzupassen. Klicken Sie auf "Speichern" um die Änderungen zu übernehmen.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Inventory Message */}
      {inventory.length === 0 && !isLoading && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-6 text-center">
            <Package className="w-12 h-12 mx-auto text-amber-500 mb-3" />
            <p className="text-amber-800 font-medium mb-2">Keine Produkte gefunden</p>
            <p className="text-amber-600 text-sm mb-4">Klicken Sie auf "Aktualisieren" um die Produkte zu laden.</p>
            <Button onClick={loadInventory} className="bg-amber-600 hover:bg-amber-700 text-white">
              <RefreshCw className="w-4 h-4 mr-2" />
              Produkte laden
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      {inventory.length > 0 && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Gesamtbestand</p>
                <p className="text-2xl font-bold text-slate-900">{getTotalStock()} Stück</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <ArrowUpCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Produkte</p>
                <p className="text-2xl font-bold text-slate-900">{inventory.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`border-slate-200 ${getLowStockCount() > 0 ? 'bg-amber-50 border-amber-200' : ''}`}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${getLowStockCount() > 0 ? 'bg-amber-100' : 'bg-slate-100'}`}>
                <AlertTriangle className={`w-6 h-6 ${getLowStockCount() > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Niedriger Bestand</p>
                <p className={`text-2xl font-bold ${getLowStockCount() > 0 ? 'text-amber-600' : 'text-slate-900'}`}>
                  {getLowStockCount()} Produkte
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Low Stock Warning */}
      {inventory.length > 0 && getLowStockCount() > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  {getLowStockCount()} Produkt(e) haben einen niedrigen Bestand (unter 10 Stück)
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  {inventory.filter(item => item.current_stock < item.warning_threshold).map(item => item.product_name).join(", ")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inventory Table */}
      {inventory.length > 0 && (
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Produkte {isSortMode && <span className="text-blue-600 font-normal">(Sortiermodus)</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                {isSortMode && <TableHead className="w-20">Sortieren</TableHead>}
                <TableHead>Produkt</TableHead>
                <TableHead className="text-center">Bestand</TableHead>
                <TableHead className="text-center">Status</TableHead>
                {!isSortMode && <TableHead className="text-right">Aktionen</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {inventory.map((item, index) => (
                <TableRow key={item.product_code} className={item.current_stock < item.warning_threshold ? 'bg-amber-50' : ''}>
                  {isSortMode && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => moveItem(index, -1)}
                          disabled={index === 0}
                          className="h-7 w-7 p-0"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => moveItem(index, 1)}
                          disabled={index === inventory.length - 1}
                          className="h-7 w-7 p-0"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                  <TableCell>
                    <div>
                      <p className="font-medium text-slate-900">{item.product_name}</p>
                      <p className="text-xs text-slate-500">{item.product_code}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={`text-lg font-bold ${item.current_stock < item.warning_threshold ? 'text-amber-600' : 'text-slate-900'}`}>
                      {item.current_stock}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    {item.current_stock < item.warning_threshold ? (
                      <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Niedrig
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                        OK
                      </Badge>
                    )}
                  </TableCell>
                  {!isSortMode && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openHistoryDialog(item)}
                        className="h-8"
                        title="Historie"
                      >
                        <History className="w-4 h-4" />
                      </Button>
                      {!isViewer && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openCorrectionDialog(item)}
                            className="h-8"
                            title="Korrigieren"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openBookingDialog(item)}
                            className="bg-green-600 hover:bg-green-700 text-white h-8"
                            title="Einbuchen"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Einbuchen
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      )}

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bestand einbuchen
            </DialogTitle>
            <DialogDescription>
              Buchen Sie eingehende Ware für "{selectedProduct?.product_name}" ein.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Aktueller Bestand</p>
              <p className="text-2xl font-bold text-slate-900">{selectedProduct?.current_stock} Stück</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="quantity">Menge *</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                value={bookingQuantity}
                onChange={(e) => setBookingQuantity(e.target.value)}
                placeholder="Anzahl eingeben..."
                className="text-lg"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason">Grund</Label>
              <Input
                id="reason"
                value={bookingReason}
                onChange={(e) => setBookingReason(e.target.value)}
                placeholder="z.B. Lieferung, Inventur..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingDialog(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleBooking}
              disabled={isBooking}
              className="bg-green-600 hover:bg-green-700"
            >
              {isBooking ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Einbuchen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {selectedProduct ? `Buchungshistorie: ${selectedProduct.product_name}` : "Alle Buchungen"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {transactions.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Keine Buchungen vorhanden</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum</TableHead>
                    {!selectedProduct && <TableHead>Produkt</TableHead>}
                    <TableHead className="text-center">Typ</TableHead>
                    <TableHead className="text-center">Menge</TableHead>
                    <TableHead>Grund</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((trans) => (
                    <TableRow key={trans.id}>
                      <TableCell className="text-sm">
                        {new Date(trans.created_at).toLocaleString("de-DE", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </TableCell>
                      {!selectedProduct && (
                        <TableCell className="text-sm">{trans.product_name}</TableCell>
                      )}
                      <TableCell className="text-center">
                        {trans.transaction_type === "in" ? (
                          <Badge className="bg-green-100 text-green-700">
                            <ArrowUpCircle className="w-3 h-3 mr-1" />
                            Eingang
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">
                            <ArrowDownCircle className="w-3 h-3 mr-1" />
                            Ausgang
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        <span className={trans.transaction_type === "in" ? "text-green-600" : "text-red-600"}>
                          {trans.transaction_type === "in" ? "+" : "-"}{trans.quantity}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{trans.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Booking Dialog */}
      <Dialog open={showQuickBookingDialog} onOpenChange={setShowQuickBookingDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-green-600" />
                Lieferung einbuchen
              </div>
            </DialogTitle>
            <DialogDescription>
              Wählen Sie ein Produkt und geben Sie die Menge ein.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="product">Produkt auswählen *</Label>
              <Select
                value={selectedProductCode}
                onValueChange={setSelectedProductCode}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Produkt auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {inventory.map((item) => (
                    <SelectItem key={item.product_code} value={item.product_code}>
                      <div className="flex items-center justify-between w-full">
                        <span>{item.product_name}</span>
                        <span className="text-xs text-slate-500 ml-2">({item.current_stock} Stk.)</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="quick-quantity">Menge *</Label>
              <Input
                id="quick-quantity"
                type="number"
                min="1"
                value={bookingQuantity}
                onChange={(e) => setBookingQuantity(e.target.value)}
                placeholder="Anzahl eingeben..."
                className="text-lg"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="quick-reason">Grund</Label>
              <Input
                id="quick-reason"
                value={bookingReason}
                onChange={(e) => setBookingReason(e.target.value)}
                placeholder="z.B. Lieferung, Inventur..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickBookingDialog(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleQuickBooking}
              disabled={isBooking}
              className="bg-green-600 hover:bg-green-700"
            >
              {isBooking ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Einbuchen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Correction Dialog */}
      <Dialog open={showCorrectionDialog} onOpenChange={setShowCorrectionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              <div className="flex items-center gap-2">
                <Pencil className="w-5 h-5 text-blue-600" />
                Bestand korrigieren
              </div>
            </DialogTitle>
            <DialogDescription>
              Korrigieren Sie den Bestand für "{selectedProduct?.product_name}".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Aktueller Bestand</p>
              <p className="text-2xl font-bold text-slate-900">{selectedProduct?.current_stock} Stück</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="correction-stock">Neuer Bestand *</Label>
              <Input
                id="correction-stock"
                type="number"
                min="0"
                value={correctionStock}
                onChange={(e) => setCorrectionStock(e.target.value)}
                placeholder="Korrigierter Bestand..."
                className="text-lg"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="correction-reason">Grund</Label>
              <Input
                id="correction-reason"
                value={correctionReason}
                onChange={(e) => setCorrectionReason(e.target.value)}
                placeholder="z.B. Inventur, Schwund, Fehler..."
              />
            </div>
            {selectedProduct && correctionStock !== "" && (
              <div className={`p-3 rounded-lg ${parseInt(correctionStock) > selectedProduct.current_stock ? 'bg-green-50' : parseInt(correctionStock) < selectedProduct.current_stock ? 'bg-red-50' : 'bg-slate-50'}`}>
                <p className="text-sm text-slate-600">Differenz:</p>
                <p className={`text-lg font-bold ${parseInt(correctionStock) > selectedProduct.current_stock ? 'text-green-600' : parseInt(correctionStock) < selectedProduct.current_stock ? 'text-red-600' : 'text-slate-600'}`}>
                  {parseInt(correctionStock) - selectedProduct.current_stock > 0 ? '+' : ''}{parseInt(correctionStock) - selectedProduct.current_stock} Stück
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCorrectionDialog(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleCorrection}
              disabled={isBooking}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isBooking ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Pencil className="w-4 h-4 mr-2" />
              )}
              Korrigieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
